/**
 * DevHandbuch — In-App Developer Documentation with Markdown rendering.
 *
 * Features:
 * - Sidebar with numbered chapter list (01-10)
 * - Full Markdown rendering with styled blockquotes, code, tables
 * - Client-side search filtering across all chapters
 * - Violet/purple theme to distinguish from user handbook
 *
 * @page /dev-handbuch, /dev-handbuch/:slug
 * @roles admin only
 * @endpoints GET /api/dev-handbuch, GET /api/dev-handbuch/:slug
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Code } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

// Custom Markdown Renderers (primary-themed for dev handbook)
const mdComponents = {
  // Blockquotes as colored info boxes
  blockquote: ({ children }) => {
    const extractText = (node) => {
      if (typeof node === 'string') return node;
      if (!node) return '';
      if (node.props?.children) {
        const c = node.props.children;
        if (Array.isArray(c)) return c.map(extractText).join('');
        return extractText(c);
      }
      return '';
    };
    const text = Array.isArray(children) ? children.map(extractText).join('') : extractText(children);

    // Tip (green)
    if (text.includes('\u{1F4A1}') || text.includes('Tipp') || text.includes('Tip')) {
      return (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-emerald-900 dark:text-emerald-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-emerald-800 dark:[&_strong]:text-emerald-100">{children}</div>
        </div>
      );
    }

    // Warning (amber)
    if (text.includes('\u26A0') || text.includes('Wichtig') || text.includes('Achtung') || text.includes('Warning')) {
      return (
        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-amber-900 dark:text-amber-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-amber-800 dark:[&_strong]:text-amber-100">{children}</div>
        </div>
      );
    }

    // Step / Note (violet)
    if (text.includes('\u{1F4CC}') || text.includes('Schritt') || text.includes('Note')) {
      return (
        <div className="bg-primary-50 dark:bg-primary-900/20 border-l-4 border-[#338edc] rounded-r-lg p-4 my-4 not-prose">
          <div className="text-sm text-primary-900 dark:text-primary-200 [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&_strong]:text-primary-800 dark:[&_strong]:text-primary-100">{children}</div>
        </div>
      );
    }

    // Default blockquote (gray)
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 border-l-4 border-[var(--border-default,#E5E7EB)] dark:border-gray-600 rounded-r-lg p-4 my-4 not-prose">
        <div className="text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] italic [&>p]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0">{children}</div>
      </div>
    );
  },

  // Headings
  h1: ({ children }) => (
    <h1 className="flex items-center gap-2 text-2xl font-bold mt-6 mb-4 pb-2 border-b border-primary-200 dark:border-primary-800 text-[var(--text-primary,#1A1A2E)]">
      <Code size={20} className="text-[#64748B] flex-shrink-0" />
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold mt-8 mb-3 pb-2 border-b border-[var(--border-default,#E5E7EB)] dark:border-gray-700 text-[var(--text-primary,#1A1A2E)]">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-medium mt-6 mb-2 text-[var(--text-primary,#1A1A2E)]">
      {children}
    </h3>
  ),

  // Lists
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-5 space-y-1.5 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-5 space-y-2 my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed">{children}</li>
  ),

  // Code
  code: ({ inline, className, children }) => {
    if (inline !== false && !className) {
      return (
        <code className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-1.5 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      );
    }
    return (
      <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto my-4">
        <code className="text-sm font-mono">{children}</code>
      </pre>
    );
  },

  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-gray-700">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-[var(--bg-inset,#F8F9FC)]">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[var(--text-secondary,#4B5563)] dark:text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] border-t border-[var(--border-table,#F3F4F6)] dark:border-gray-800">
      {children}
    </td>
  ),

  // HR
  hr: () => <hr className="my-8 border-[var(--border-default,#E5E7EB)] dark:border-gray-700" />,

  // Paragraphs
  p: ({ children }) => (
    <p className="my-3 text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)] leading-relaxed">{children}</p>
  ),

  // Bold
  strong: ({ children }) => (
    <strong className="font-semibold text-[var(--text-primary,#1A1A2E)]">{children}</strong>
  ),

  // Links
  a: ({ href, children }) => (
    <a href={href} className="text-[#338edc] dark:text-primary-400 hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

export default function DevHandbuch() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Load all chapters
  useEffect(() => {
    fetch(`${API}/api/dev-handbuch`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const sorted = (d.chapters || []).sort((a, b) => a.sort_order - b.sort_order);
        setChapters(sorted);
        if (!slug && sorted.length > 0) {
          navigate(`/dev-handbuch/${sorted[0].slug}`, { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Select chapter when slug changes
  useEffect(() => {
    if (!slug || chapters.length === 0) return;
    const ch = chapters.find(c => c.slug === slug);
    setSelectedChapter(ch || null);
  }, [slug, chapters]);

  // Client-side search filtering
  const filteredChapters = useMemo(() => {
    if (!search.trim()) return chapters;
    const q = search.toLowerCase();
    return chapters.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.content.toLowerCase().includes(q)
    );
  }, [chapters, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-6 w-6 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-72' : 'w-0 overflow-hidden'} border-r border-line bg-surface flex-shrink-0 flex flex-col transition-all duration-200`}>
        <div className="p-4 border-b border-line">
          <div className="flex items-center gap-2 mb-3">
            <Code className="w-4 h-4 text-[#338edc]" />
            <span className="text-sm font-semibold text-content">{t('devHandbook.title', 'Dev Handbook')}</span>
          </div>
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('devHandbook.searchPlaceholder', 'Search chapters...')}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-line bg-surface-elevated text-content placeholder-content-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          <div className="text-[10px] font-semibold text-content-tertiary uppercase tracking-widest px-2 mb-2">
            {t('devHandbook.chapters', 'Chapters')} ({filteredChapters.length})
          </div>
          {filteredChapters.map(c => {
            const numPrefix = c.slug.match(/^(\d+)/);
            const displayNum = numPrefix ? numPrefix[1].padStart(2, '0') : '';
            return (
              <button
                key={c.slug}
                onClick={() => { navigate(`/dev-handbuch/${c.slug}`); setSearch(''); }}
                className={`block w-full text-left px-3 py-2 rounded-lg text-sm mb-0.5 transition-colors ${
                  slug === c.slug
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-[#338edc] dark:text-primary-400 font-medium'
                    : 'hover:bg-surface-hover text-content-secondary'
                }`}
              >
                <span className="text-content-tertiary font-mono text-xs mr-2">{displayNum}</span>
                {c.title}
              </button>
            );
          })}
          {filteredChapters.length === 0 && search && (
            <div className="text-sm text-content-secondary px-2 py-4 text-center">
              {t('devHandbook.noResults', 'No chapters found.')}
            </div>
          )}
        </nav>
      </aside>

      {/* Toggle Sidebar */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="flex-shrink-0 w-6 flex items-center justify-center hover:bg-surface-hover text-content-tertiary transition-colors"
        title={sidebarOpen ? t('manual.collapseNav', 'Collapse') : t('manual.expandNav', 'Expand')}
      >
        <svg className={`w-4 h-4 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path d="m15 19-7-7 7-7" />
        </svg>
      </button>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        {selectedChapter ? (
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-content-secondary mb-2">
                <span className="bg-primary-100 dark:bg-primary-900/30 text-[#338edc] dark:text-primary-400 px-2 py-0.5 rounded text-xs font-mono">
                  {selectedChapter.slug.match(/^(\d+)/)?.[1]?.padStart(2, '0') || '??'}
                </span>
                <span>{t('devHandbook.devDocumentation', 'Developer Documentation')}</span>
              </div>
              <h1 className="text-xl font-semibold text-content">{selectedChapter.title}</h1>
            </div>
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{selectedChapter.content}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-content-secondary">
            <div className="text-center">
              <Code className="w-10 h-10 text-[#338edc] mx-auto mb-3 opacity-70" />
              <p>{t('devHandbook.selectChapter', 'Select a chapter from the sidebar')}</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
